import React from 'react';

import { StyleSheet, View } from 'react-native';

import { ILocation } from '@rbi-ctg/frontend';
import { t } from 'state/intl/translations';
import { primitive } from 'styles/constants';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';

import { AmenityIcon, InnerHeader, InnerText, PhoneNumber } from '../styled';
import type { FormattedStoreAddress, RestaurantDetailItem } from '../types';

import { DirectionsLink } from './directions-link';
import { ServiceAvailabilityItems } from './service-availability-items';

interface RestaurantDetailsSectionProps {
  activeCoordinates: ILocation | null;
  formattedAddress: FormattedStoreAddress | null;
  formattedPhoneNumber: string | null;
  mapPosition: ILocation;
  phoneNumber: string | null | undefined;
  serviceAvailabilityItems: RestaurantDetailItem[];
}

export const RestaurantDetailsSection = ({
  activeCoordinates,
  formattedAddress,
  formattedPhoneNumber,
  mapPosition,
  phoneNumber,
  serviceAvailabilityItems,
}: RestaurantDetailsSectionProps) => (
  <View style={styles.restaurantDetails}>
    <InnerHeader
      accessibilityLevel={1}
      variant="headerTwo"
      testID="restaurant-details"
      nativeID="modal-heading"
      style={styles.sectionHeading}
    >
      {t('restaurantDetails')}
    </InnerHeader>
    <View style={styles.detailContainer}>
      {formattedAddress && (
        <View style={styles.detailRow}>
          <AmenityIcon
            variant="locationFilled"
            size="$5"
            style={styles.contactIcon}
            {...hiddenAccessibilityPlatformProps}
          />
          <View style={styles.addressContent}>
            {formattedAddress.streetAddress && (
              <InnerText testID="address" style={styles.streetAddressText}>
                {formattedAddress.streetAddress}
              </InnerText>
            )}
            <View style={styles.addressSecondaryLine}>
              {formattedAddress.cityStatePostalCode && (
                <InnerText
                  testID="address-city-state-postal-code"
                  style={styles.addressSecondaryText}
                >
                  {formattedAddress.cityStatePostalCode}.
                </InnerText>
              )}
              <DirectionsLink
                activeCoordinates={activeCoordinates}
                mapPosition={mapPosition}
                color={primitive.$bbqBrown}
              />
            </View>
          </View>
        </View>
      )}
      {formattedPhoneNumber && (
        <View style={styles.detailRow}>
          <AmenityIcon
            variant="mobile"
            size="$5"
            style={styles.contactIcon}
            {...hiddenAccessibilityPlatformProps}
          />
          <PhoneNumber
            accessibilityLabel={`${t('phoneNumber')} ${formattedPhoneNumber}`}
            testID="phoneNumber"
            to={`tel:${phoneNumber}`}
          >
            {formattedPhoneNumber}
          </PhoneNumber>
        </View>
      )}
    </View>
    {serviceAvailabilityItems.length > 0 && (
      <ServiceAvailabilityItems items={serviceAvailabilityItems} />
    )}
  </View>
);

const styles = StyleSheet.create({
  restaurantDetails: {
    padding: 24,
  },
  sectionHeading: {
    marginBottom: 0,
  },
  detailContainer: {
    paddingVertical: 14,
  },
  detailRow: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  contactIcon: {
    marginRight: 12,
    marginTop: 1,
  },
  addressContent: {
    flex: 1,
  },
  addressSecondaryLine: {
    alignItems: 'baseline',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  streetAddressText: {
    fontSize: 13,
  },
  addressSecondaryText: {
    fontSize: 13,
    marginRight: 8,
  },
});
